// =========================================================
// Animacje (GSAP + ScrollTrigger): intro hero strony głównej, kółka krążące po okręgu, wejścia sekcji
// Jeden język ruchu dla całego serwisu (MOTION): te same easingi, czasy i przesunięcia, a w każdej sekcji ta sama
// kolejność — nadtytuł → nagłówek linia po linii → tekst → przyciski → media; karty jedna po drugiej.
// prefers-reduced-motion: bez animacji, wszystko widoczne od razu.
// =========================================================

import {gsap} from 'gsap';
import {ScrollTrigger} from 'gsap/ScrollTrigger';
import {SplitText} from 'gsap/SplitText';
import {DrawSVGPlugin} from 'gsap/DrawSVGPlugin';

gsap.registerPlugin(ScrollTrigger, SplitText, DrawSVGPlugin);

const PENDING = 'anim-pending';
const enabled = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// skrypt ładuje się w <head>: treść ukryta do ustawienia stanów początkowych (styles/utils/_animations.scss);
// zabezpieczenie — gdyby skrypt nie wystartował, treść pokaże się po 3 s
if (enabled) {
    document.documentElement.classList.add(PENDING);
    setTimeout(() => document.documentElement.classList.remove(PENDING), 3000);
}

const MOTION = {
    ease: 'expo.out',
    duration: 1.2,
    distance: 40,     // przesunięcie elementów wjeżdżających od dołu (px)
    stagger: 0.09,    // odstęp między kolejnymi elementami jednej grupy
    maxStagger: 0.9,  // łączny czas kolejkowania dużej grupy
    start: 'top 80%', // element startuje, gdy jego góra mija 80% wysokości okna
};

// role elementów w sekcjach #main — pierwsza pasująca wygrywa; elementy wewnątrz kart i mediów ruszają razem z nimi
const ROLES = [
    ['item', [
        '.choose-card', '.expert-card', '.tech-card', '.link-card', '.review-card', '.practice-card', '.article-card',
        '.evidence-card', '.evidence-group', '.product-card', '.post-card', '.spec-tile', '.feature-list__item', '.faq-item',
        '.protocol', '.pdp-step', '.pdp-ingredient', '.technology-block', '.technology-pillars__item', '.data-row',
        '.partner-card', '.pdp-feature', '.study-card',
    ]],
    ['media', ['.video', '[class*="__media"]', '[class*="__photo"]', '[class*="__diagram"]']],
    ['eyebrow', ['.eyebrow', '[class*="__eyebrow"]', '[class*="__kicker"]']],
    ['heading', ['.heading', 'h1', 'h2', '.pdp-statement__line']],
    ['text', [
        '.lead', 'p[class*="__text"]', 'p[class*="__intro"]', 'p[class*="__subtitle"]', 'p[class*="__tagline"]',
        '.woocommerce-product-gallery', '.pdp-summary__pricing', '.pdp-summary__description', '.pdp-summary__benefits',
        '.pdp-summary__actions', '.wpcf7', '.finder-form', '.partner-results__count', '.partner-results__cities',
        '.text-section__content', '.ingredient-facts',
    ]],
    ['button', ['.btn']],
];

const SKIP = '.home-hero, .slick-slider, [data-anim-skip]';
const CLEAR = 'opacity,visibility,transform,transition';

// stan początkowy elementu → funkcja startu animacji (zwraca tween / timeline)
function fadeIn(el, from) {
    gsap.set(el, {autoAlpha: 0, transition: 'none', ...from});

    return () => gsap.to(el, {autoAlpha: 1, x: 0, y: 0, duration: MOTION.duration, ease: MOTION.ease, clearProps: CLEAR});
}

function lines(el) {
    let tween;
    let played = false;

    // autoSplit: przy zmianie szerokości linie dzielą się od nowa, a postęp animacji zostaje
    SplitText.create(el, {
        type: 'lines',
        mask: 'lines',
        linesClass: 'anim-line',
        autoSplit: true,
        onSplit(self) {
            tween = gsap.from(self.lines, {
                yPercent: 125,
                duration: MOTION.duration * 1.15,
                ease: MOTION.ease,
                stagger: 0.08,
                paused: ! played,
                // po wejściu nagłówek wraca do pierwotnego HTML
                onComplete: () => gsap.delayedCall(0, () => self.revert()),
            });

            return tween;
        },
    });

    return () => {
        played = true;

        return tween.play();
    };
}

function media(el) {
    const inner = el.querySelector('img, video');
    gsap.set(el, {autoAlpha: 0, clipPath: 'inset(8% 6% 8% 6% round 24px)', transition: 'none'});
    if (inner) {
        gsap.set(inner, {scale: 1.12, transition: 'none'});
    }

    return () => {
        const tl = gsap.timeline({defaults: {duration: MOTION.duration * 1.3, ease: MOTION.ease}});
        tl.to(el, {autoAlpha: 1, duration: 0.5, ease: 'none'}, 0)
            .to(el, {clipPath: 'inset(0% 0% 0% 0% round 0px)', clearProps: CLEAR + ',clipPath'}, 0);
        if (inner) {
            tl.to(inner, {scale: 1, clearProps: CLEAR}, 0);
        }

        return tl;
    };
}

const SETUP = {
    item: (el) => fadeIn(el, {y: MOTION.distance * 1.2}),
    media,
    eyebrow: (el) => fadeIn(el, {x: -16}),
    heading: lines,
    text: (el) => fadeIn(el, {y: MOTION.distance * 0.6}),
    button: (el) => fadeIn(el, {y: MOTION.distance * 0.4}),
};

function collectTargets(main) {
    const found = new Map();
    ROLES.forEach(([role, selectors]) => {
        main.querySelectorAll(selectors.join(',')).forEach((el) => {
            if ( ! found.has(el) && ! el.closest(SKIP)) {
                found.set(el, role);
            }
        });
    });

    const insideTarget = (el) => {
        for (let parent = el.parentElement; parent && parent !== main; parent = parent.parentElement) {
            if (found.has(parent)) {
                return true;
            }
        }

        return false;
    };

    // tylko widoczne (bez wariantów ukrytych na tym ekranie), w kolejności dokumentu
    return Array.from(found)
        .filter(([el]) => el.getClientRects().length > 0 && ! insideTarget(el))
        .sort(([a], [b]) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
}

function sectionReveals(main) {
    const targets = collectTargets(main);
    const play = new Map(targets.map(([el, role]) => [el, SETUP[role](el)]));

    // elementy, które wchodzą w ekran razem, ruszają kolejno
    ScrollTrigger.batch(targets.map(([el]) => el), {
        start: MOTION.start,
        once: true,
        onEnter(batch) {
            const step = Math.min(MOTION.stagger, MOTION.maxStagger / batch.length);
            const tl = gsap.timeline();
            batch.forEach((el, index) => tl.add(play.get(el)(), index * step));
        },
    });

    // rozwijane FAQ / akordeony zmieniają wysokość strony — przeliczenie punktów startu
    if ('ResizeObserver' in window) {
        let timer;
        new ResizeObserver(() => {
            clearTimeout(timer);
            timer = setTimeout(() => ScrollTrigger.refresh(), 200);
        }).observe(main);
    }
}

// ---------- hero strony głównej ----------

function heroIntro(hero) {
    const q = gsap.utils.selector(hero);
    const title = hero.querySelector('.home-hero__title');
    const ring = hero.querySelector('.home-hero__ring circle');
    const tl = gsap.timeline({defaults: {ease: MOTION.ease, duration: 1.4}});

    tl.from(q('.home-hero__sphere'), {autoAlpha: 0, scale: 0.85, duration: 2}, 0);
    if (ring) {
        // okrąg rysuje się od góry, zgodnie z ruchem wskazówek
        gsap.set(ring, {rotation: -90, transformOrigin: '50% 50%'});
        tl.from(ring, {drawSVG: '0%', duration: 2.2, ease: 'power2.inOut'}, 0.1);
    }
    tl.from(q('.home-hero__half'), {autoAlpha: 0, xPercent: -20, duration: 1.8}, 0.4)
        .from(q('.home-hero__pill'), {autoAlpha: 0, scale: 0.9, duration: 1.6}, 0.9)
        .from(q('.home-hero__orbit--outer .home-hero__dot'), {autoAlpha: 0, scale: 0, stagger: 0.15, duration: 0.9, ease: 'back.out(2.5)'}, 1.2)
        .from(q('.home-hero__orbit--inner .home-hero__dot'), {autoAlpha: 0, scale: 0, stagger: 0.15, duration: 0.9, ease: 'back.out(2.5)'}, 1.4)
        .from(q('.home-hero__label'), {autoAlpha: 0, y: 16, stagger: 0.12}, 1.2);

    if (title) {
        const split = SplitText.create(title, {type: 'lines', mask: 'lines', linesClass: 'anim-line'});
        tl.from(split.lines, {yPercent: 125, stagger: 0.1, duration: 1.5, onComplete: () => split.revert()}, 0.15);
    }
    tl.from(q('.home-hero__subtitle'), {autoAlpha: 0, y: 24}, 0.55)
        // fromTo: przycisk wraca do stylów z CSS (hover) — clearProps w from() zostawiłby go niewidocznym
        .fromTo(q('.home-hero__cta'), {autoAlpha: 0, y: 24}, {autoAlpha: 1, y: 0, clearProps: 'opacity,visibility,transform'}, 0.7);
}

// kółka krążą po liniach: zewnętrzne zgodnie z ruchem wskazówek, wewnętrzne (pod szkłem półkuli) w przeciwną stronę;
// biała półkula „szkła” po intro cały czas chowa się całkiem (odsłania kulę) i wraca (przycięcie od prawej
// krawędzi — łuk półkuli bez zniekształceń)
function heroOrbits(hero) {
    const spins = [
        gsap.to(hero.querySelector('.home-hero__orbit--outer'), {rotation: 360, duration: 60, ease: 'none', repeat: -1}),
        gsap.to(hero.querySelector('.home-hero__orbit--inner'), {rotation: -360, duration: 45, ease: 'none', repeat: -1}),
    ];

    const half = hero.querySelector('.home-hero__half');
    if (half) {
        spins.push(gsap.fromTo(half, {clipPath: 'inset(0% 0% 0% 0%)'}, {
            clipPath: 'inset(0% 100% 0% 0%)',
            duration: 4.5,
            ease: 'sine.inOut',
            repeat: -1,
            yoyo: true,
            repeatDelay: 0.4,
            delay: 2.4,
        }));
    }

    // poza ekranem ruch stoi
    ScrollTrigger.create({
        trigger: hero,
        start: 'top bottom',
        end: 'bottom top',
        onToggle: ({isActive}) => spins.forEach((spin) => (isActive ? spin.play() : spin.pause())),
    });
}

function start() {
    const hero = document.querySelector('.home-hero');
    if (hero) {
        heroIntro(hero);
        heroOrbits(hero);
    }

    const main = document.getElementById('main');
    if (main) {
        sectionReveals(main);
    }

    document.documentElement.classList.remove(PENDING);
}

function init() {
    if ( ! enabled) {
        return;
    }

    // podział nagłówków na linie po załadowaniu fontów (najwyżej 1.2 s czekania)
    const fonts = document.fonts ? document.fonts.ready : Promise.resolve();
    Promise.race([fonts, new Promise((resolve) => setTimeout(resolve, 1200))])
        .then(start)
        .catch(() => document.documentElement.classList.remove(PENDING));
}

export default {init};
